import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import * as Sentry from '@sentry/react';

/**
 * Custom hook for filtering data based on user's property access restrictions.
 * 
 * This hook automatically fetches the user's accessible properties and provides
 * utility functions for filtering data based on those restrictions.
 * 
 * Usage in dashboards:
 * ```jsx
 * const { accessiblePropertyIDs, isUnrestricted, isLoading, filterByProperty } = usePropertyFilter();
 * 
 * // Filter data array by property
 * const filteredData = data.filter(item => filterByProperty(item.propertyID));
 * ```
 */
export const usePropertyFilter = () => {
    const { apiFetch, user } = useAuth();
    const [accessiblePropertyIDs, setAccessiblePropertyIDs] = useState(null);
    const [isUnrestricted, setIsUnrestricted] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    /**
     * Fetches the user's accessible properties from the API.
     * Memoized to prevent recreation on every render.
     */
    const fetchAccessibleProperties = useCallback(async () => {
        // Support both camelCase (userID) and PascalCase (UserID) depending on API response shape
        const resolvedUserId = user?.userID ?? user?.UserID;
        if (!resolvedUserId) {
            setIsLoading(false);
            return;
        }
        
        try {
            setIsLoading(true);
            const response = await apiFetch(`/api/Permissions/my-accessible-properties/${resolvedUserId}`);
            const data = await response.json();

            if (data.success && data.data) {
                setIsUnrestricted(data.data.unrestricted);
                
                if (!data.data.unrestricted && data.data.properties) {
                    // Extract property IDs from the properties array
                    const propertyIDs = data.data.properties.map(p => p.propertyID);
                    setAccessiblePropertyIDs(propertyIDs);
                } else {
                    // Unrestricted access - no filtering needed
                    setAccessiblePropertyIDs(null);
                }
            }
        } catch (err) {
            Sentry.captureException(err);
            console.error('Error fetching accessible properties:', err);
            setError(err);
            // On error, default to unrestricted to avoid breaking the dashboard
            setIsUnrestricted(true);
            setAccessiblePropertyIDs(null);
        } finally {
            setIsLoading(false);
        }
    }, [apiFetch, user?.userID, user?.UserID]);

    useEffect(() => {
        // Support both camelCase (userID) and PascalCase (UserID) depending on API response shape
        const resolvedUserId = user?.userID ?? user?.UserID;
        if (!resolvedUserId) {
            setIsLoading(false);
            return;
        }

        fetchAccessibleProperties();
    }, [user?.userID, user?.UserID, fetchAccessibleProperties]);

    /**
     * Checks if the user has access to a specific property.
     * Memoized to maintain stable reference across renders.
     * 
     * @param {number} propertyID - The property ID to check
     * @returns {boolean} - True if user has access, false otherwise
     */
    const hasAccessToProperty = useCallback((propertyID) => {
        if (isUnrestricted || accessiblePropertyIDs === null) {
            return true; // Unrestricted access
        }
        return accessiblePropertyIDs.includes(propertyID);
    }, [isUnrestricted, accessiblePropertyIDs]);

    /**
     * Filters an array of data based on a property ID field.
     * Memoized to maintain stable reference across renders and prevent
     * infinite loops when used as a dependency in useCallback/useEffect.
     * 
     * @param {Array} data - Array of data objects
     * @param {string} propertyField - Name of the field containing the property ID (default: 'propertyID')
     * @returns {Array} - Filtered data array
     */
    const filterData = useCallback((data, propertyField = 'propertyID') => {
        if (!data || !Array.isArray(data)) {
            return data;
        }

        if (isUnrestricted || accessiblePropertyIDs === null) {
            return data; // No filtering needed
        }

        return data.filter(item => {
            const propertyID = item[propertyField];
            return propertyID && accessiblePropertyIDs.includes(propertyID);
        });
    }, [isUnrestricted, accessiblePropertyIDs]);

    /**
     * Filters an array of data based on a nested property ID field.
     * Memoized to maintain stable reference across renders.
     * 
     * @param {Array} data - Array of data objects
     * @param {Function} getPropertyID - Function to extract property ID from item
     * @returns {Array} - Filtered data array
     */
    const filterDataByGetter = useCallback((data, getPropertyID) => {
        if (!data || !Array.isArray(data)) {
            return data;
        }

        if (isUnrestricted || accessiblePropertyIDs === null) {
            return data; // No filtering needed
        }

        return data.filter(item => {
            const propertyID = getPropertyID(item);
            return propertyID && accessiblePropertyIDs.includes(propertyID);
        });
    }, [isUnrestricted, accessiblePropertyIDs]);

    /**
     * Gets a WHERE clause filter for SQL queries (for MDA database queries).
     * Returns a string like "PropertyID IN (1,2,3)" or null if unrestricted.
     * Memoized for stable reference.
     * 
     * @param {string} propertyField - Name of the property ID field (default: 'PropertyID')
     * @returns {string|null} - SQL WHERE clause or null
     */
    const getSqlFilter = useCallback((propertyField = 'PropertyID') => {
        if (isUnrestricted || accessiblePropertyIDs === null || accessiblePropertyIDs.length === 0) {
            return null; // No filtering needed
        }

        const ids = accessiblePropertyIDs.join(',');
        return `${propertyField} IN (${ids})`;
    }, [isUnrestricted, accessiblePropertyIDs]);

    /**
     * Gets the list of accessible property IDs as a comma-separated string.
     * Useful for API requests that need property filters.
     * Memoized for stable reference.
     * 
     * @returns {string|null} - Comma-separated property IDs or null if unrestricted
     */
    const getPropertyIDsString = useMemo(() => {
        if (isUnrestricted || accessiblePropertyIDs === null || accessiblePropertyIDs.length === 0) {
            return null;
        }
        return accessiblePropertyIDs.join(',');
    }, [isUnrestricted, accessiblePropertyIDs]);

    return {
        // State
        accessiblePropertyIDs,
        isUnrestricted,
        isLoading,
        error,
        
        // Utility functions (memoized for stable references)
        hasAccessToProperty,
        filterData,
        filterDataByGetter,
        getSqlFilter,
        getPropertyIDsString,
        
        // Alias for backward compatibility
        filterByProperty: hasAccessToProperty
    };
};
